import { motion } from "framer-motion";
import { Monitor, Server, Cpu, Users, Database, FileText } from "lucide-react";

const NODES = [
  { icon: Monitor, label: "UI" },
  { icon: Server, label: "Backend" },
  { icon: Cpu, label: "FastAPI + Models" },
  { icon: Users, label: "AI Agents" },
  { icon: Database, label: "MongoDB" },
  { icon: FileText, label: "Report" },
];

export default function Architecture() {
  return (
    <section id="architecture" className="relative py-24 md:py-32 bg-slate-50 overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1771015138120-319470ad8f04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxtZWRpY2FsJTIwdGVjaG5vbG9neSUyMGFic3RyYWN0fGVufDB8fHx8MTc3NjQ0NTgzM3ww&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50/80 via-slate-50/70 to-slate-50" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">
            <span className="font-mono-num">04</span> · System Architecture
          </div>
          <h2 className="font-heading mt-4 text-4xl sm:text-5xl tracking-tight font-light text-slate-900">
            A layered pipeline, <br />
            <span className="text-slate-400">end to end.</span>
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-0">
          {NODES.map((n, i) => {
            const Icon = n.icon;
            return (
              <motion.div
                key={n.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="relative flex flex-col"
              >
                <div className="glass rounded-2xl p-5 md:p-6 h-full">
                  <div className="h-10 w-10 rounded-lg bg-white ring-1 ring-slate-200 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-slate-700" />
                  </div>
                  <div className="mt-5 text-[10px] uppercase tracking-[0.25em] font-mono-num text-slate-400">
                    Layer 0{i + 1}
                  </div>
                  <div className="font-heading text-lg text-slate-900 mt-1">{n.label}</div>
                </div>
                {i < NODES.length - 1 && (
                  <div className="hidden md:block absolute right-[-10px] top-1/2 -translate-y-1/2 z-10">
                    <div className="h-6 w-6 rounded-full bg-white ring-1 ring-slate-200 flex items-center justify-center text-slate-400 font-mono-num text-[10px]">
                      →
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {[
            { k: "Frontend", v: "React 19 · Tailwind · Framer Motion" },
            { k: "Backend", v: "FastAPI · Motor · JWT cookies" },
            { k: "Storage", v: "MongoDB · in-memory image processing" },
          ].map((x) => (
            <div key={x.k} className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="text-[10px] uppercase tracking-[0.25em] font-mono-num text-slate-500">{x.k}</div>
              <div className="mt-1 text-slate-800 text-sm">{x.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
